import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  OnInit,
  OnDestroy,
  AfterViewInit,
  HostListener,
} from '@angular/core';
import {
  CompositeFilterDescriptor,
  GroupDescriptor,
  SortDescriptor,
  State,
} from '@progress/kendo-data-query';
import {
  GridModule,
  ExcelModule,
  PDFModule,
  DataStateChangeEvent,
  SelectionEvent,
  CellClickEvent,
  RowClassArgs as KendoRowClassArgs,
  GridComponent as KendoGridComponent,
  GridDataResult,
  PageChangeEvent,
  GroupableSettings,
} from '@progress/kendo-angular-grid';
import { debounceTime, distinctUntilChanged, Subject, Subscription, Observable, of } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { ExcelExportModule } from '@progress/kendo-angular-excel-export';
import { PDFExportModule } from '@progress/kendo-angular-pdf-export';
import { mergeGridConfig } from '../../configs/grid-defaults.config';
import { GRID_CONSTANTS } from '../../constants/grid.constant';
import { GridColumnType } from '../../enums/grid.enum';
import { GridUtils } from '../../helpers/grid.helper';
import {
  GridConfig,
  GridStateChangeEvent,
  GridCellClickEvent,
  GridActionClickEvent,
  ToolbarButton,
  PaginationConfig,
} from '../../models/grid.model';
import { GridServiceInstance, GridService } from '../../services/grid.service';
import {
  DetailGridConfig,
  DetailRowExpandEvent,
  DetailActionConfig,
  GridColumnConfig,
  GridActionConfig,
  DetailColumnConfig,
  GridCellDblClickEvent,
} from '../../types/grid.type';
import {
  GridFilterComponent,
  FilterFieldConfig,
  FilterChangeEvent,
} from '../grid-filter/grid-filter.component';
import {
  FilterableConfig,
  SelectableConfig,
  SortableConfig,
} from '../../../../../models/grid-config.interface';
import { Router } from '@angular/router';

export interface GridFilterConfig {
  showLabels?: boolean;
  showClearButton?: boolean;
  showClearButtonText?: boolean;
  layout?: 'horizontal' | 'vertical';
  initialValues?: { [key: string]: any };
  debounceTime?: number;
}

@Component({
  selector: 'app-common-grid',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    GridModule,
    ExcelModule,
    PDFModule,
    InputsModule,
    ButtonsModule,
    DropDownsModule,
    DialogsModule,
    ExcelExportModule,
    PDFExportModule,
    GridFilterComponent,
  ],
  templateUrl: './common-grid.component.html',
  styleUrl: './common-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommonGridComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('kendoGrid') kendoGrid!: KendoGridComponent;

  // Basic Configuration
  @Input() config!: GridConfig;
  @Input() data: GridDataResult = GridUtils.createEmptyDataResult();
  @Input() loading: boolean = false;
  @Input() apiEndpoint?: string;
  @Input() gridId?: string;

  // Detail Grid Configuration
  @Input() detailConfig?: DetailGridConfig;
  @Input() enableDetailGrid: boolean = false;

  // Grid Filter Configuration
  @Input() showGridFilter: boolean = false;
  @Input() filterFields: FilterFieldConfig[] = [];
  @Input() filterConfig?: GridFilterConfig;

  // Responsive height inputs
  @Input() autoHeight: boolean = true;
  @Input() offsetHeight: number = 0;
  @Input() minHeight: number = 400;
  @Input() maxHeight?: number;

  @ContentChild('loadingTemplate') loadingTemplate?: TemplateRef<any>;
  @ContentChild('noRecordsTemplate') noRecordsTemplate?: TemplateRef<any>;
  @ContentChild('detailTemplate') detailTemplate?: TemplateRef<any>;

  // Master Grid Events
  @Output() dataStateChange = new EventEmitter<GridStateChangeEvent>();
  @Output() selectionChange = new EventEmitter<any[]>();
  @Output() cellClick = new EventEmitter<GridCellClickEvent>();
  @Output() actionClick = new EventEmitter<GridActionClickEvent>();
  @Output() pageChange = new EventEmitter<PageChangeEvent>();
  @Output() sortChange = new EventEmitter<SortDescriptor[]>();
  @Output() filterChange = new EventEmitter<CompositeFilterDescriptor>();
  @Output() groupChange = new EventEmitter<GroupDescriptor[]>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() toolbarButtonClick = new EventEmitter<ToolbarButton>();
  @Output() addClick = new EventEmitter<void>();
  @Output() editClick = new EventEmitter<any>();
  @Output() deleteClick = new EventEmitter<any>();
  @Output() cellDblClick = new EventEmitter<GridCellDblClickEvent>();

  // Grid Filter Events
  @Output() gridFilterChange = new EventEmitter<FilterChangeEvent>();
  @Output() gridFilterClear = new EventEmitter<void>();

  // Detail Grid Events
  @Output() detailExpand = new EventEmitter<DetailRowExpandEvent>();
  @Output() detailCollapse = new EventEmitter<DetailRowExpandEvent>();
  @Output() detailActionClick = new EventEmitter<{
    action: DetailActionConfig;
    item: any;
  }>();
  @Output() detailCellClick = new EventEmitter<any>();
  @Output() detailSelectionChange = new EventEmitter<any>();

  gridData: GridDataResult = GridUtils.createEmptyDataResult();
  gridState: State & { search?: string } = {};
  processedColumns: GridColumnConfig[] = [];
  selectedKeys: any[] = [];
  leftToolbarButtons: ToolbarButton[] = [];
  rightToolbarButtons: ToolbarButton[] = [];
  paginationConfig!: PaginationConfig;
  searchValue: string = '';

  // Detail Grid Properties
  expandedDetailKeys: any[] = [];
  detailDataCache = new Map<any, Observable<GridDataResult>>();

  // Image caching to prevent blinking - CRITICAL FIX
  private readonly imageSrcCache = new Map<string, string>();
  private readonly imageAltCache = new Map<string, string>();
  private readonly imageTitleCache = new Map<string, string>();

  // Responsive height properties
  calculatedHeight: number = 600;
  private resizeTimeout?: any;

  private gridService?: GridServiceInstance;
  private readonly searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;
  private readonly serviceSubscriptions: Subscription[] = [];

  constructor(
    private readonly gridServiceFactory: GridService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.initializeGrid();
    this.setupSearch();
    this.setupGridService();
    this.calculateGridHeight();
    this.initializeDetailGrid();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this.initializeGrid();
    }
    if (changes['data']) {
      this.gridData = this.data;
      // Clear image cache when data changes
      this.clearImageCache();
      this.cdr.markForCheck();
    }
    if (changes['apiEndpoint'] || changes['gridId']) {
      this.setupGridService();
    }
    if (
      changes['autoHeight'] ||
      changes['offsetHeight'] ||
      changes['minHeight'] ||
      changes['maxHeight']
    ) {
      this.calculateGridHeight();
    }
    if (changes['detailConfig'] || changes['enableDetailGrid']) {
      this.initializeDetailGrid();
    }
  }

  ngAfterViewInit(): void {
    Promise.resolve().then(() => {
      this.calculateGridHeight();
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
    this.searchSubscription?.unsubscribe();
    this.serviceSubscriptions.forEach((sub) => sub.unsubscribe());

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    if (this.gridService && this.gridId) {
      this.gridServiceFactory.destroyGrid(this.gridId);
    }

    // Clear caches
    this.clearImageCache();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.autoHeight) return;

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(() => {
      this.calculateGridHeight();
      this.cdr.markForCheck();
    }, 150);
  }

  // ==================== INITIALIZATION METHODS ====================

  private calculateGridHeight(): void {
    if (!this.autoHeight) {
      this.calculatedHeight = this.resolveConfiguredHeight();
      return;
    }

    const viewportHeight = window.innerHeight;
    const toolbarHeight = this.hasToolbar() ? 70 : 0;
    const filterHeight = this.getFilterHeight();
    const bufferHeight = 40;

    let availableHeight =
      viewportHeight - this.offsetHeight - toolbarHeight - filterHeight - bufferHeight;
    this.calculatedHeight = this.applyHeightConstraints(availableHeight);
  }
  private resolveConfiguredHeight(): number {
    const height = this.config?.height;

    if (height === undefined || height === null) {
      return 600;
    }

    if (typeof height === 'number') {
      return height;
    }

    const parsedHeight = Number(height);
    return !isNaN(parsedHeight) ? parsedHeight : 600;
  }

  private getFilterHeight(): number {
    console.log(this.showGridFilter, this.filterFields);
    if (!this.showGridFilter || !this.filterFields?.length) {
      return 0;
    }

    const filterElement = document.querySelector('.grid-filter-form');
    if (filterElement) {
      const rect = filterElement.getBoundingClientRect();
      const filterHeight = rect.height + 20;
      return filterHeight;
    }

    return 120; // fallback
  }

  private applyHeightConstraints(height: number): number {
    let result = height;

    if (this.minHeight && height < this.minHeight) {
      result = this.minHeight;
    }

    if (this.maxHeight && height > this.maxHeight) {
      result = this.maxHeight;
    }

    return result;
  }

  private initializeGrid(): void {
    this.config = mergeGridConfig(this.config ?? {});
    this.processedColumns = this.processColumns();
    this.processToolbarButtons();
    this.setPaginationConfig();
    this.setGridState();
    this.gridData = this.data;
    this.cdr.markForCheck();
  }

  private processColumns(): GridColumnConfig[] {
    if (!this.config?.columns?.length) return [];

    const globalSortable = typeof this.config.sortable === 'boolean' ? this.config.sortable : false;
    const globalFilterable =
      typeof this.config.filterable === 'boolean' ? this.config.filterable : false;
    const globalGroupable =
      typeof this.config.groupable === 'boolean' ? this.config.groupable : false;

    return this.config.columns.map((col) => ({
      ...col,
      sortable: typeof col.sortable === 'boolean' ? col.sortable : globalSortable,
      filterable: typeof col.filterable === 'boolean' ? col.filterable : globalFilterable,
      groupable: typeof col.groupable === 'boolean' ? col.groupable : globalGroupable,
    }));
  }

  private processToolbarButtons(): void {
    if (!this.config?.toolbarButtons?.length) {
      this.leftToolbarButtons = [];
      this.rightToolbarButtons = [];
      return;
    }

    this.leftToolbarButtons = this.config.toolbarButtons.filter(
      (btn) => btn.position === 'left' || !btn.position
    );
    this.rightToolbarButtons = this.config.toolbarButtons.filter((btn) => btn.position === 'right');
  }

  private setPaginationConfig(): void {
    const defaultConfig: PaginationConfig = {
      enabled: true,
      pageSize: 10,
      pageSizes: [5, 10, 20, 50],
      showTotalCount: true,
      buttonCount: 5,
      previousNext: true,
    };

    if (typeof this.config.pageable === 'boolean') {
      this.paginationConfig = {
        ...defaultConfig,
        enabled: this.config.pageable,
      };
    } else if (typeof this.config.pageable === 'object') {
      this.paginationConfig = {
        ...defaultConfig,
        ...this.config.pageable,
      };
    } else {
      this.paginationConfig = defaultConfig;
    }
  }

  private setGridState(): void {
    this.gridState = {
      skip: this.config?.skip ?? 0,
      take: this.paginationConfig.pageSize,
      sort: this.config?.sort ?? [],
      filter: this.convertToCompositeFilter(this.config?.filter),
      group: this.config?.group ?? [],
    };
  }

  private convertToCompositeFilter(filter?: any): CompositeFilterDescriptor | undefined {
    if (!filter) return undefined;

    // If it's already a CompositeFilterDescriptor
    if ('logic' in filter && 'filters' in filter) {
      return filter as CompositeFilterDescriptor;
    }

    // Convert FilterDescriptor to CompositeFilterDescriptor
    return {
      logic: 'and',
      filters: [filter],
    };
  }

  private setupSearch(): void {
    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((searchValue) => {
        this.gridState = {
          ...this.gridState,
          skip: 0,
          search: searchValue,
        };
        this.searchChange.emit(searchValue);
        this.emitDataStateChange();
      });
  }

  private setupGridService(): void {
    if (!this.apiEndpoint || !this.gridId) {
      return;
    }

    this.gridService = this.gridServiceFactory.createGrid(
      {
        gridId: this.gridId,
        apiEndpoint: this.apiEndpoint,
        autoLoad: true,
        debounceTime: GRID_CONSTANTS.SEARCH_DEBOUNCE_TIME,
      },
      this.gridState
    );

    const dataSubscription = this.gridService.data$.subscribe((data) => {
      this.gridData = data;
      this.cdr.markForCheck();
    });

    const loadingSubscription = this.gridService.loading$.subscribe((loading) => {
      this.loading = loading;
      this.cdr.markForCheck();
    });

    this.serviceSubscriptions.push(dataSubscription, loadingSubscription);
  }

  private initializeDetailGrid(): void {
    if (!this.enableDetailGrid || !this.detailConfig) {
      return;
    }

    this.expandedDetailKeys = [];
    this.detailDataCache.clear();
  }

  // ==================== IMAGE CACHE METHODS ====================

  private clearImageCache(): void {
    this.imageSrcCache.clear();
    this.imageAltCache.clear();
    this.imageTitleCache.clear();
  }

  private generateImageCacheKey(dataItem: any, columnField: string | undefined): string {
    // Use item ID if available, otherwise use a combination of field and relevant data
    const itemId = dataItem?.id ?? dataItem?.ID ?? dataItem?.Id;
    return `${itemId ?? 'noId'}_${columnField ?? 'noField'}`;
  }

  // ==================== INITIALS AVATAR GENERATION ====================

  /**
   * Extracts initials from a full name
   * Examples:
   * - "Dhruv Jain" -> "DJ"
   * - "John Doe Smith" -> "JD"
   * - "Alice" -> "AL"
   */
  private extractInitials(name: string): string {
    if (!name || typeof name !== 'string') {
      return '?';
    }

    const nameParts = name.trim().split(/\s+/);

    if (nameParts.length === 0) {
      return '?';
    }

    if (nameParts.length === 1) {
      // Single name: take first 2 characters or just 1
      return nameParts[0].substring(0, 2).toUpperCase();
    }

    // Multiple names: take first letter of first and last name
    const firstInitial = nameParts[0].charAt(0).toUpperCase();
    const lastInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();

    return firstInitial + lastInitial;
  }

  /**
   * Gets a consistent color based on a string (for initials avatar background)
   */
  private getColorFromString(str: string): string {
    const colors = [
      '#1abc9c',
      '#2ecc71',
      '#3498db',
      '#9b59b6',
      '#34495e',
      '#16a085',
      '#27ae60',
      '#2980b9',
      '#8e44ad',
      '#2c3e50',
      '#f39c12',
      '#e67e22',
      '#e74c3c',
      '#95a5a6',
      '#d35400',
      '#c0392b',
      '#7f8c8d',
      '#2c2c54',
      '#218c74',
      '#b33939',
    ];

    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash;
    }

    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }

  /**
   * Generates a data URI for an SVG avatar with initials
   */
  private generateInitialsAvatar(
    initials: string,
    size: number = 100,
    backgroundColor?: string,
    textColor: string = '#ffffff'
  ): string {
    const bgColor = backgroundColor ?? this.getColorFromString(initials);

    // Calculate font size based on image size and initials length
    const fontSize = initials.length === 1 ? size * 0.5 : size * 0.4;

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <rect width="${size}" height="${size}" fill="${bgColor}"/>
        <text
          x="50%"
          y="50%"
          dominant-baseline="central"
          text-anchor="middle"
          font-family="Arial, sans-serif"
          font-size="${fontSize}"
          font-weight="600"
          fill="${textColor}"
        >${initials}</text>
      </svg>
    `;

    // Encode SVG to data URI
    const encoded = encodeURIComponent(svg).replace(/'/g, '%27').replace(/"/g, '%22');

    return `data:image/svg+xml,${encoded}`;
  }

  // ==================== GRID FILTER METHODS ====================

  /**
   * Handle grid filter change event
   */
  onGridFilterChange(event: FilterChangeEvent): void {
    // Update grid state with the Kendo filter
    if (event.kendoFilter) {
      this.gridState = {
        ...this.gridState,
        filter: event.kendoFilter,
        skip: 0, // Reset to first page when filtering
      };
    } else {
      // Remove filter if null
      const { filter, ...stateWithoutFilter } = this.gridState;
      this.gridState = {
        ...stateWithoutFilter,
        skip: 0,
      };
    }

    // Emit the filter change event
    this.gridFilterChange.emit(event);

    // Trigger data state change
    this.emitDataStateChange();

    // If using grid service, load data
    if (this.gridService) {
      this.gridService.loadData();
    }

    this.cdr.markForCheck();
  }

  /**
   * Handle grid filter clear event
   */
  onGridFilterClear(): void {
    // Remove filter from grid state
    const { filter, ...stateWithoutFilter } = this.gridState;
    this.gridState = {
      ...stateWithoutFilter,
      skip: 0,
    };

    // Emit the clear event
    this.gridFilterClear.emit();

    // Trigger data state change
    this.emitDataStateChange();

    // If using grid service, load data
    if (this.gridService) {
      this.gridService.loadData();
    }

    this.cdr.markForCheck();
  }

  // ==================== EVENT HANDLERS ====================

  onDataStateChange(state: DataStateChangeEvent): void {
    this.gridState = {
      ...this.gridState,
      skip: state.skip,
      take: state.take,
      sort: state.sort,
      group: state.group,
    };

    // Don't override filter if it comes from grid-filter component
    if (state.filter && !this.showGridFilter) {
      this.gridState.filter = state.filter;
    }

    this.emitDataStateChange();

    if (this.gridService) {
      this.gridService.loadData();
    }
  }

  private emitDataStateChange(): void {
    const event: GridStateChangeEvent = {
      skip: this.gridState.skip ?? 0,
      take: this.gridState.take ?? 10,
      sort: this.gridState.sort,
      filter: this.gridState.filter,
      group: this.gridState.group,
      search: this.gridState.search,
    };

    this.dataStateChange.emit(event);

    if (this.gridState.sort) {
      this.sortChange.emit(this.gridState.sort);
    }

    if (this.gridState.filter) {
      this.filterChange.emit(this.gridState.filter);
    }

    if (this.gridState.group) {
      this.groupChange.emit(this.gridState.group);
    }
  }

  onSelectionChange(event: SelectionEvent): void {
    const selectedRows = event.selectedRows || [];
    this.selectedKeys = selectedRows.map((row) => row.dataItem);
    this.selectionChange.emit(this.selectedKeys);
  }

  onCellClick(event: CellClickEvent): void {
    const column = this.processedColumns[event.columnIndex];
    const cellClickEvent: GridCellClickEvent = {
      dataItem: event.dataItem,
      column: column,
      rowIndex: event.rowIndex,
      columnIndex: event.columnIndex,
    };
    this.cellClick.emit(cellClickEvent);
  }

  onActionClick(action: GridActionConfig, dataItem: any, rowIndex: number = 0): void {
    const event: GridActionClickEvent = {
      action,
      dataItem,
      rowIndex,
    };

    this.actionClick.emit(event);

    if (action.type === 'edit') {
      this.editClick.emit(dataItem);
    } else if (action.type === 'delete') {
      this.deleteClick.emit(dataItem);
    }

    if (action.handler) {
      action.handler(dataItem, rowIndex);
    } else if (action.onClick) {
      action.onClick(dataItem, rowIndex);
    }
  }

  onSearchChange(value: string): void {
    this.searchSubject.next(value);
  }

  onToolbarButtonClick(button: ToolbarButton): void {
    this.toolbarButtonClick.emit(button);

    if (button.handler) {
      button.handler();
    } else if (button.onClick) {
      button.onClick();
    }
  }

  onGroupChange(groups: GroupDescriptor[]): void {
    this.gridState = {
      ...this.gridState,
      group: groups,
    };
    this.groupChange.emit(groups);
    this.emitDataStateChange();
  }

  onCheckboxChange(event: Event, dataItem: any, column: GridColumnConfig): void {
    const checkbox = event.target as HTMLInputElement;
    dataItem[column.field!] = checkbox.checked;

    if (column.onChange) {
      column.onChange(dataItem, checkbox.checked);
    }

    this.cdr.markForCheck();
  }

  // ==================== DETAIL GRID METHODS ====================

  getDetailData(dataItem: any): Observable<GridDataResult> {
    if (!this.detailConfig?.detailDataFn && !this.detailConfig?.detailDataProvider) {
      return of(GridUtils.createEmptyDataResult());
    }

    const cacheKey = dataItem.id ?? dataItem;

    if (!this.detailDataCache.has(cacheKey)) {
      const detailData$ = this.detailConfig.detailDataFn
        ? this.detailConfig.detailDataFn(dataItem)
        : this.detailConfig.detailDataProvider!(dataItem);
      this.detailDataCache.set(cacheKey, detailData$);
    }

    return this.detailDataCache.get(cacheKey)!;
  }

  onDetailActionClick(action: DetailActionConfig, item: any): void {
    this.detailActionClick.emit({ action, item });

    if (action.handler) {
      action.handler(item);
    } else if (action.onClick) {
      action.onClick(item);
    }
  }

  onDetailCellClickEvent(event: CellClickEvent): void {
    this.detailCellClick.emit(event);
  }

  onDetailSelectionChangeEvent(event: SelectionEvent): void {
    this.detailSelectionChange.emit(event);
  }

  // ==================== EXPORT METHODS ====================

  exportToExcel(): void {
    if (this.kendoGrid) {
      this.kendoGrid.saveAsExcel();
    }
  }

  exportToPDF(): void {
    if (this.kendoGrid) {
      this.kendoGrid.saveAsPDF();
    }
  }

  // ==================== UTILITY METHODS ====================

  getRowClass(context: KendoRowClassArgs): any {
    if (!this.config?.rowClass) {
      return {};
    }

    if (typeof this.config.rowClass === 'function') {
      return this.config.rowClass(context.dataItem, context.index);
    }

    return this.config.rowClass;
  }

  getBadgeClass(value: any, column: GridColumnConfig): string {
    const badgeConfig = column.badgeConfig;

    if (!badgeConfig) {
      return 'badge-secondary';
    }

    if (badgeConfig.colorMap?.[value]) {
      return badgeConfig.colorMap[value];
    }

    return badgeConfig.defaultColor ?? 'badge-secondary';
  }

  isActionVisible(action: GridActionConfig | DetailActionConfig, dataItem: any): boolean {
    if (action.visible === undefined) {
      return true;
    }

    return typeof action.visible === 'function' ? action.visible(dataItem) : action.visible;
  }

  isActionDisabled(action: GridActionConfig | DetailActionConfig, dataItem: any): boolean {
    if (action.disabled === undefined) {
      return false;
    }

    return typeof action.disabled === 'function' ? action.disabled(dataItem) : action.disabled;
  }

  isToolbarButtonVisible(button: ToolbarButton): boolean {
    if (button.visible === undefined) {
      return true;
    }

    if (typeof button.visible === 'function') {
      return button.visible();
    }

    return !!button.visible;
  }

  isToolbarButtonDisabled(button: ToolbarButton): boolean {
    if (button.disabled === undefined) {
      return false;
    }

    if (typeof button.disabled === 'function') {
      return button.disabled();
    }

    return !!button.disabled;
  }

  getSortableConfig(): boolean | SortableConfig {
    const sortable = this.config?.sortable;

    // Only enable if explicitly set to true or configured as an object
    if (!sortable) {
      return false;
    }

    if (typeof sortable === 'boolean') {
      return sortable ? { enabled: true, allowUnsort: true, mode: 'single' } : false;
    }

    return sortable;
  }

  getFilterableConfig(): boolean | any {
    const filterable = this.config?.filterable;

    // Only enable if explicitly set to true or configured as an object
    if (!filterable) {
      return false;
    }

    if (typeof filterable === 'boolean') {
      return filterable
        ? {
            enabled: true,
            extra: true,
            mode: 'menu',
          }
        : false;
    }

    return filterable;
  }

  getGroupableConfig(): boolean | GroupableSettings {
    if (!this.config?.groupable) return false;

    if (typeof this.config.groupable === 'boolean') {
      return { showFooter: false };
    }

    return {
      ...this.config.groupable,
      showFooter: this.config.groupable.showFooter ?? false,
    };
  }

  getSelectableConfig(): boolean | SelectableConfig {
    const selectable = this.config?.selectable;

    if (!selectable) {
      return false;
    }

    if (typeof selectable === 'boolean') {
      return {
        enabled: true,
        mode: 'single',
        checkboxOnly: false,
      };
    }

    return selectable;
  }

  private getSelectableMode(): string {
    if (!this.config?.selectable) return 'single';
    if (typeof this.config.selectable === 'boolean') return 'single';
    return this.config.selectable.mode ?? 'single';
  }

  getGridHeight(): number {
    if (this.autoHeight) {
      return this.calculatedHeight;
    }
    if (this.config?.height === undefined || this.config?.height === null) {
      return 600;
    }
    return GridUtils.calculateGridHeight(this.config);
  }

  getExcelFileName(): string {
    return GridUtils.generateFileName(
      this.config?.exportable && typeof this.config.exportable === 'object'
        ? this.config.exportable.fileName
        : undefined,
      'xlsx'
    );
  }

  getPdfFileName(): string {
    return GridUtils.generateFileName(
      this.config?.exportable && typeof this.config.exportable === 'object'
        ? this.config.exportable.fileName
        : undefined,
      'pdf'
    );
  }

  getHeaderClass(column: GridColumnConfig | DetailColumnConfig): string {
    return column.type === GridColumnType.BOOLEAN
      ? (column.headerClass ?? 'text-start')
      : (column.headerClass ?? '');
  }

  getColumnClass(column: GridColumnConfig | DetailColumnConfig): string {
    if (column.type === GridColumnType.BOOLEAN) {
      return typeof column.className === 'string' ? column.className : 'text-start';
    }
    return typeof column.className === 'string' ? column.className : '';
  }

  getColumnWidth(column: GridColumnConfig): number {
    if (typeof column.width === 'number') {
      return column.width;
    }
    if (typeof column.width === 'string') {
      return parseInt(column.width, 10) || 150;
    }
    return 150;
  }

  isExcelExportEnabled(): boolean {
    return typeof this.config.exportable === 'object' && this.config.exportable.excel === true;
  }

  isPdfExportEnabled(): boolean {
    return typeof this.config.exportable === 'object' && this.config.exportable.pdf === true;
  }

  hasToolbar(): boolean {
    return !!(
      this.config.toolbarButtons?.length ??
      this.config.exportable ??
      this.config.showSearch !== false
    );
  }

  formatCellValue(value: any, column: GridColumnConfig | DetailColumnConfig): string {
    if (value === null || value === undefined) {
      return '-';
    }
    return GridUtils.formatCellValue(value, column as GridColumnConfig);
  }

  getColumnFormat(column: GridColumnConfig): string {
    return column.format ?? '';
  }

  getFilterConfig(): any {
    return this.gridState.filter;
  }

  formatDate(value: any, format?: string): string {
    return GridUtils.formatDate(value, format);
  }

  formatCurrency(value: any, format?: string): string {
    return GridUtils.formatCurrency(value, format);
  }

  formatNumber(value: any, format?: string): string {
    return GridUtils.formatNumber(value, format);
  }

  getActionClass(action: GridActionConfig, dataItem: any): string {
    let className = action.className ?? 'btn-outline-secondary';

    if (action.type) {
      switch (action.type) {
        case 'edit':
          className += ' btn-outline-primary';
          break;
        case 'delete':
          className += ' btn-outline-danger';
          break;
        case 'view':
          className += ' btn-outline-info';
          break;
        default:
          className += ' btn-outline-secondary';
      }
    }

    return className;
  }

  getActionTooltip(action: GridActionConfig | DetailActionConfig, dataItem: any): string {
    if (typeof action.tooltip === 'function') {
      return (action.tooltip as any)(dataItem);
    }
    return action.tooltip ?? action.text ?? '';
  }

  getActionIcon(action: GridActionConfig | DetailActionConfig, dataItem: any): string {
    if (typeof action.icon === 'function') {
      return (action.icon as any)(dataItem);
    }
    return action.icon ?? '';
  }

  getActionText(action: GridActionConfig | DetailActionConfig, dataItem: any): string {
    if (typeof action.text === 'function') {
      return (action.text as any)(dataItem);
    }
    return action.text ?? '';
  }

  getPaginationConfig(): boolean | PaginationConfig {
    const pagination = this.paginationConfig;

    if (!this.config?.pageable || !pagination.enabled) {
      return false;
    }

    return {
      enabled: true,
      buttonCount: pagination.buttonCount ?? 5,
      info: pagination.showTotalCount !== false,
      type: 'numeric',
      pageSizes: pagination.pageSizes ?? [5, 10, 20, 50],
      previousNext: pagination.previousNext !== false,
      showTotalCount: pagination.showTotalCount,
      pageSize: pagination.pageSize,
      showPageSizeSelector: pagination.showPageSizeSelector,
      responsive: pagination.responsive,
    };
  }

  // ==================== IMAGE HANDLING METHODS (ENHANCED WITH INITIALS) ====================

  getImageSrc(dataItem: any, column: GridColumnConfig | DetailColumnConfig): string {
    const cacheKey = this.generateImageCacheKey(dataItem, column.field);

    // Return from cache if available
    if (this.imageSrcCache.has(cacheKey)) {
      return this.imageSrcCache.get(cacheKey)!;
    }

    const imageConfig = column.imageConfig;
    const imageFields = imageConfig?.sourceFields ?? [
      column.field,
      'image',
      'imageUrl',
      'logo',
      'avatar',
    ];

    // Try to find an actual image URL first
    for (const field of imageFields) {
      if (field && dataItem[field]) {
        const imageSrc = dataItem[field];
        this.imageSrcCache.set(cacheKey, imageSrc);
        return imageSrc;
      }
    }

    // NEW: Generate initials avatar if enabled and no image found
    if (imageConfig?.generateInitials) {
      const nameFields = imageConfig.nameFields ?? [
        'name',
        'fullName',
        'displayName',
        'userName',
        'title',
      ];

      let nameValue = '';
      for (const nameField of nameFields) {
        if (dataItem[nameField]) {
          nameValue = dataItem[nameField];
          break;
        }
      }

      if (nameValue) {
        const initials = this.extractInitials(nameValue);
        const size = imageConfig.initialsConfig?.size ?? 100;
        const bgColor = imageConfig.initialsConfig?.backgroundColor;
        const textColor = imageConfig.initialsConfig?.textColor ?? '#ffffff';

        const avatarSrc = this.generateInitialsAvatar(initials, size, bgColor, textColor);
        this.imageSrcCache.set(cacheKey, avatarSrc);
        return avatarSrc;
      }
    }

    // Fallback to default image
    const defaultImage = imageConfig?.defaultImage ?? '../assets/images/default/default-image.png';
    this.imageSrcCache.set(cacheKey, defaultImage);
    return defaultImage;
  }

  getImageAlt(dataItem: any, column: GridColumnConfig | DetailColumnConfig): string {
    const cacheKey = this.generateImageCacheKey(dataItem, column.field);

    // Return from cache if available
    const cachedAlt = this.imageAltCache.get(cacheKey);
    if (cachedAlt) return cachedAlt;

    const imageConfig = column.imageConfig;

    const altText =
      (imageConfig?.altTextField && dataItem[imageConfig.altTextField]) ||
      dataItem.name ||
      dataItem.title ||
      dataItem.label ||
      'Image';

    // Cache the result
    this.imageAltCache.set(cacheKey, altText);
    return altText;
  }

  getImageTitle(dataItem: any, column: GridColumnConfig | DetailColumnConfig): string {
    const cacheKey = this.generateImageCacheKey(dataItem, column.field);

    // Return from cache if available
    if (this.imageTitleCache.has(cacheKey)) {
      return this.imageTitleCache.get(cacheKey)!;
    }

    const imageConfig = column.imageConfig;
    let titleText: string;

    if (imageConfig?.titleTextField && dataItem[imageConfig.titleTextField]) {
      titleText = dataItem[imageConfig.titleTextField];
    } else {
      titleText = this.getImageAlt(dataItem, column);
    }

    // Cache the result
    this.imageTitleCache.set(cacheKey, titleText);
    return titleText;
  }

  getImageClass(column: GridColumnConfig | DetailColumnConfig): string {
    const imageConfig = column.imageConfig;
    const classes = ['grid-image'];

    if (imageConfig?.shape === 'circle' || imageConfig?.shape === 'round') {
      classes.push('image-circle');
    } else if (imageConfig?.shape === 'square') {
      classes.push('image-square');
    }

    if (imageConfig?.bordered) {
      classes.push('image-bordered');
    }

    if (imageConfig?.clickable) {
      classes.push('clickable');
    }

    if (imageConfig?.className) {
      classes.push(imageConfig.className);
    }

    return classes.join(' ');
  }

  onImageError(event: Event, column: GridColumnConfig | DetailColumnConfig): void {
    const img = event.target as HTMLImageElement;
    const imageConfig = column.imageConfig;

    // NEW: Try to generate initials avatar on error if enabled
    if (imageConfig?.generateInitials && !img.src.startsWith('data:image/svg')) {
      // Get the data item from the event (you may need to pass it differently)
      // For now, we'll just use the default image
      const defaultImage = imageConfig?.defaultImage ?? '/assets/images/default-image.png';
      img.src = defaultImage;
    } else {
      const defaultImage = imageConfig?.defaultImage ?? '/assets/images/default-image.png';
      if (img.src !== defaultImage) {
        img.src = defaultImage;
      }
    }
  }

  getLinkUrl(dataItem: any, column: GridColumnConfig): string {
    return dataItem['filePath'] ?? '#';
  }

  getColorValue(color: any): string {
    if (!color) {
      return '#cccccc';
    }

    if (typeof color === 'string') {
      if (color.startsWith('#')) {
        return color;
      }

      if (color.startsWith('rgb')) {
        return color;
      }

      const namedColors = [
        'red',
        'blue',
        'green',
        'yellow',
        'orange',
        'purple',
        'pink',
        'black',
        'white',
        'gray',
        'brown',
        'cyan',
        'magenta',
        'lime',
        'navy',
        'teal',
        'aqua',
        'maroon',
        'olive',
        'silver',
        'fuchsia',
      ];

      if (namedColors.includes(color.toLowerCase())) {
        return color;
      }

      return color;
    }

    if (typeof color === 'object') {
      return color.value ?? color.hex ?? color.color ?? '#cccccc';
    }

    return '#cccccc';
  }

  onCellDblClick(
    event: Event,
    dataItem: any,
    column: any,
    rowIndex: number,
    columnIndex: number
  ): void {
    event.preventDefault();

    const dblClickConfig = column.dblClick;

    const dblClickEvent = {
      dataItem,
      column,
      rowIndex,
      columnIndex,
      field: column.field,
      config: dblClickConfig,
    };

    if (!dblClickConfig?.enabled) return;

    switch (dblClickConfig.action) {
      case 'navigate':
        this.handleNavigateAction(dataItem, column, dblClickConfig);
        break;
      case 'dialog':
        this.cellDblClick.emit(dblClickEvent);
        break;
      case 'custom':
        if (dblClickConfig.handler) {
          dblClickConfig.handler(dataItem, column, rowIndex);
        }
        break;
    }
  }

  private handleNavigateAction(dataItem: any, column: any, config: any): void {
    if (!config.routeTemplate) return;

    let route = config.routeTemplate;

    const placeholderRegex = /:([a-zA-Z_]\w*)/g;

    route = route.replace(placeholderRegex, (_match: string, fieldName: string) => {
      return String(this.getNestedValue(dataItem, fieldName) ?? '');
    });

    // Build query params
    let queryParams: any = {};
    if (config.queryParams) {
      queryParams =
        typeof config.queryParams === 'function'
          ? config.queryParams(dataItem)
          : config.queryParams;
    }

    // Navigate
    if (config.target === '_blank') {
      const url = this.router.serializeUrl(this.router.createUrlTree([route], { queryParams }));
      window.open(url, '_blank');
    } else {
      this.router.navigate([route], { queryParams });
    }
  }

  private getNestedValue(obj: any, path: string): any {
    if (!path || !obj) return undefined;
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  hasDblClickEnabled(column: any): boolean {
    return column.dblClick?.enabled === true;
  }

  getCellClasses(column: any, dataItem: any): { [key: string]: boolean } {
    const classes: { [key: string]: boolean } = {};

    if (column.dblClick?.enabled) {
      classes['dblclick-enabled'] = true;
      if (column.dblClick.showCursor !== false) classes['cursor-pointer'] = true;
      if (column.dblClick.highlightOnHover !== false) classes['dblclick-hover'] = true;
      if (column.dblClick.hoverClass) classes[column.dblClick.hoverClass] = true;
    }

    return classes;
  }

  getCellDblClickTooltip(column: any, dataItem: any): string {
    if (!column.dblClick?.enabled) return '';

    const tooltip = column.dblClick.tooltip;
    if (!tooltip) {
      switch (column.dblClick.action) {
        case 'navigate':
          return 'Double-click to view details';
        case 'dialog':
          return 'Double-click to open';
        default:
          return 'Double-click for action';
      }
    }

    return typeof tooltip === 'function' ? tooltip(dataItem) : tooltip;
  }
}
